import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../../../firebase';
import { fetchAssignedTimeSlots } from '../../../services/timeSlotService';
import { fetchLatestSchedule } from '../../../services/teacherViewData';
import { buildRotationLookup } from '../../../services/rotationLookup';
import { addDays, buildLessonNudges, LOOKBACK_DAYS } from '../../../services/lessonNudgesModel';
import { expandOnto, lessonOccurrence, ymd } from '../../../utils/occurrenceExpansion';

const TICK_MS = 30 * 1000;

const parseYmd = (s) => {
	const [y, m, d] = s.split('-').map(Number);
	return new Date(y, m - 1, d);
};

/**
 * The live list of lessons the signed-in teacher still owes an answer for.
 *
 * Reads, and what each costs:
 *  - the assigned timeSlots, the released rotation and the test-student ids,
 *    ONCE per studio day (today's occurrences and who teaches them);
 *  - a live listener on `lessons` for the lookback window (~a week of
 *    records). It is live on purpose: answering a nudge writes the record,
 *    the listener sees it, and the row leaves every open device at once.
 *
 * A failed read degrades to fewer nudges, never to a wrong one: no rotation
 * means today's lessons cannot be placed and are left out, exactly as the
 * model leaves out any lesson it cannot attribute.
 */
export default function useLessonNudges({ myTeacherId, enabled }) {
	const [nowMs, setNowMs] = useState(() => Date.now());
	const todayStr = ymd(new Date(nowMs));
	const [slots, setSlots] = useState([]);
	const [rotation, setRotation] = useState(null);
	const [testIds, setTestIds] = useState(() => new Set());
	const [records, setRecords] = useState([]);

	useEffect(() => {
		if (!enabled) return undefined;
		const t = setInterval(() => setNowMs(Date.now()), TICK_MS);
		return () => clearInterval(t);
	}, [enabled]);

	// Recurring sources: once per studio day.
	useEffect(() => {
		if (!enabled) return undefined;
		let live = true;
		(async () => {
			const [sl, run, tests] = await Promise.allSettled([
				fetchAssignedTimeSlots(),
				fetchLatestSchedule(),
				getDocs(query(collection(db, 'students'), where('isTestStudent', '==', true))),
			]);
			if (!live) return;
			if (sl.status === 'fulfilled') setSlots(sl.value);
			else console.error('Lesson nudges: time slots failed to load:', sl.reason);
			if (run.status === 'fulfilled') setRotation(buildRotationLookup(run.value.assignments));
			else console.error('Lesson nudges: rotation failed to load:', run.reason);
			if (tests.status === 'fulfilled') setTestIds(new Set(tests.value.docs.map((d) => d.id)));
		})();
		return () => { live = false; };
	}, [enabled, todayStr]);

	// Session records for the window, live.
	useEffect(() => {
		if (!enabled) return undefined;
		const fromStr = addDays(todayStr, -(LOOKBACK_DAYS - 1));
		const q = query(collection(db, 'lessons'), where('date', '>=', fromStr), where('date', '<=', todayStr));
		return onSnapshot(
			q,
			(snap) => setRecords(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
			(err) => console.error('Lesson nudges: lessons listener failed:', err),
		);
	}, [enabled, todayStr]);

	const todayOccurrences = useMemo(() => {
		const date = parseYmd(todayStr);
		const out = [];
		slots.forEach((slot) => expandOnto(slot, date, todayStr, date.getDay(), lessonOccurrence, out));
		return out;
	}, [slots, todayStr]);

	return useMemo(() => (enabled ? buildLessonNudges({
		myTeacherId,
		todayStr,
		nowMs,
		todayOccurrences,
		records,
		rotation,
		excludedStudentIds: testIds,
	}) : []), [enabled, myTeacherId, todayStr, nowMs, todayOccurrences, records, rotation, testIds]);
}
